import React, { useEffect, useState, useRef, useContext, forwardRef, useImperativeHandle } from "react";
import { toast } from "react-toastify";
import { useSet } from "seed/gql";
import { useGet, usePost } from "seed/api";
import { SET_EXECUTION } from "seed/gql/queries";
import { useAuth } from "components/util/helpers/providers/AuthProvider";
import { ControllerActions, ControllerType } from "components/util/controller";
import { HostControllerContext } from "components/presentation/main/Main.lib.communication";
import l from "components/util/l";
import { attachFiles, getAttachmentOptions, isMultipleAttachment } from "./Conversation.lib.attachments";
import { buildChatOptions, buildPendingUserMessage } from "./Conversation.lib.messages";
import useChatMessages from "./Conversation.lib.useMessages";
import useChatScroll from "./Conversation.lib.useScroll";
import useChatStream, { isStreamReadySignal } from "./Conversation.lib.useStream";
import View from "./Conversation.view";
import "styles/css/agent/Agent.css";

const TERMINAL_EXECUTION_STATUSES = ["OK", "FAILED", "ABORTED"];

// The one status that means the chat is busy. Everything else — it finished, it broke,
// or it is parked waiting for an answer — is the user's turn again.
const RUNNING_EXECUTION_STATUS = "RUNNING";

const Conversation = forwardRef(({
  chat,
  executionVariables,
  flow,
  functions = {},
  historyComponent,
  isExecutionChat,
  isHistoryEnabled = false,
  isReadOnly = false,
  rulesComponent,
  showScreenshotAttachment = false,
  slotId,
  onClickNewChat = () => { },
  onClickToggleHistory = () => { },
  onClickToggleRules = () => { },
  onExecutionMessage = () => { },
}, ref) => {

  const user = useAuth();
  const isDeveloper = user?.is_developer;

  const executionRoomName = `EXECUTION_STATUS_${flow?.id}`;
  const chatRoomName = `CHAT_STATUS_${chat.id}`;
  const executionSessionId = sessionStorage.getItem("execution_session_id");

  const listRef = useRef();
  const formRef = useRef();
  
  // The execution currently in flight, so cancel/send can report against it.
  const runningExecutionRef = useRef();
  // An execution is marked read once; the websocket resends its status on every step.
  const readExecutionIdsRef = useRef(new Set());
  // Set from the stream hook once it exists, so the message hook can reach it.
  const endIfSilentRef = useRef(null);

  // Present only while this chat is portaled into the project's app.
  const host = useContext(HostControllerContext);

  const [isLoading, setIsLoading] = useState(true);
  const [isCancelable, setIsCancelable] = useState(false);
  // From the socket, not the messages: a process that keeps writing to the chat leaves a
  // plain bot message last, and the stop button must not vanish while it still runs.
  const [isExecutionRunning, setIsExecutionRunning] = useState(false);
  
  // The capture the composer is waiting on, with the resolver of the promise it awaits.
  // The overlay belongs to the conversation rather than to the composer because it has
  // to cover the layout the user is pointing at, not the input they armed it from.
  const [screenshotRequest, setScreenshotRequest] = useState(null);
  // Same contract for the camera: the composer awaits a photo, or null if the user leaves.
  const [cameraRequest, setCameraRequest] = useState(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  const {
    scrollDirection,
    scrollToBottom,
    scrollToMessage,
    onChatScroll,
    onClickScrollBtn,
  } = useChatScroll(listRef);

  const {
    messages,
    setMessages,
    isInitialized,
    refetchMessages,
    refetchMessagesDebounced,
  } = useChatMessages(chat.id, {
    // The stored reply is the end of the turn: it stops the spinner, and it closes a
    // stream that opened without ever sending a token (see useStream.endIfSilent).
    // Held in a ref because the stream hook is created below this one.
    onBotMessagesArrived: () => {
      setIsLoading(false);
      endIfSilentRef.current?.();
    },
    onMessagesAppended: (messageId) =>
      messageId ? scrollToMessage(messageId) : scrollToBottom(),
  });

  const {
    activityText,
    isStreaming,
    thinkingText,
    streamingText,
    isRenderedByStream,
    endIfSilent,
    startStream,
    markStreamReady,
  } = useChatStream(chat.id, {
    onDone: () => {
      setIsLoading(false);
      refetchMessages();
    },
  });

  endIfSilentRef.current = endIfSilent;

  const reqModels = useGet("/chats/get_llm_models");

  const [callSetExecutionRead] = useSet(SET_EXECUTION);

  const [callCancelChatAction] = usePost("/chats/cancel_action", {
    onCompleted: () => {
      refetchMessages();
      onExecutionMessage({ status: "ABORTED", execution_id: runningExecutionRef.current?.id });
    },
  });

  const [callRollbackAction] = usePost("/chats/rollback_action", {
    onCompleted: refetchMessages,
  });

  const [callSendMessage] = usePost("/chats/send_message", {
    onCompleted: () => {
      refetchMessages();
      onExecutionMessage({ status: "SENT", execution_id: runningExecutionRef.current?.id });
    },
    onError: () => {
      refetchMessages();
      onExecutionMessage({ status: "FAILED", execution_id: runningExecutionRef.current?.id });
    },
  });

  const [callRepeatExecution] = usePost("/chats/repeat_execution", {
    onCompleted: refetchMessages,
  });

  const [callRegenerateMessage] = usePost("/chats/regenerate_message", {
    onCompleted: refetchMessages,
    onError: refetchMessages,
  });

  // The opening spinner is cleared by the first bot message that arrives — so a chat with
  // none coming spins forever: the flow writes nothing when the chat is created, and the
  // thread opens empty. The first fetch coming back is the end of that wait.
  useEffect(() => {
    if (isInitialized) setIsLoading(false);
  }, [isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // An action is cancelable while the chat is still waiting on the backend: a
  // non-default template is applied, an action is pending, or the placeholder shows.
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const appliedTemplate = (lastMessage?.templateApplied ?? [])[0]?.templateName;
    const isLoadingMessage = lastMessage?.settings?.is_loading_message ?? false;
    setIsCancelable(Boolean(
      (appliedTemplate && appliedTemplate != "default") ||
      lastMessage?.action?.type != null ||
      isLoadingMessage
    ));
  }, [messages]);

  const markExecutionRead = (executionId) => {
    if (!executionId || readExecutionIdsRef.current.has(executionId)) return;
    readExecutionIdsRef.current.add(executionId);
    callSetExecutionRead({ id: executionId, isRead: true });
  };

  const onChatMessageReceived = ({ data }) => {

    if (isStreamReadySignal(data)) {
      markStreamReady();
      setIsLoading(false);
      return;
    }

    // The backend can prefill the composer instead of sending a message.
    const details = data?.data || {};
    if (details.type == "SET_INPUT") {
      formRef.current?.setFieldValue("message", details.plain_message || "");
      formRef.current?.setFieldValue("attachments", details.attachments || []);
      return;
    }

    // A running process can reach whoever renders this chat by name, so it can act
    // on the screen at the moment it matters instead of only when the run ends.
    if (details.type == "EXECUTE_FUNCTION") {
      functions[details.name]?.(details.parameters || {});
      return;
    }

    // A UI tool: the agent is asking the project's app to paint one of its registered
    // components and is waiting on what the user does with it. The chat only carries the
    // request across — the app owns the screen, and the host owns the ids that resume
    // the run (see Main.lib.actions).
    if (details.type == "SHOW_COMPONENT" || details.type == "HIDE_COMPONENT") {
      if (details.session_id && details.session_id != executionSessionId) return;
      onComponentRequest(details);
      return;
    }

    if (data?.session_id && data?.session_id != executionSessionId) return;
    markExecutionRead(data?.execution_id);
    refetchMessagesDebounced();
  };

  const onExecutionMessageReceived = ({ data }) => {

    // The room carries every run of the flow, and a Studio run shares this tab's session id.
    if (data.chat_id != chat.id) return;

    const isTerminal = TERMINAL_EXECUTION_STATUSES.includes(data.status);
    runningExecutionRef.current = isTerminal ? null : data;

    if (data?.session_id && data?.session_id != executionSessionId) return;

    if (data.type == "EXECUTION_STATUS") {
      onExecutionMessage(data);
      markExecutionRead(data.execution_id);
      refetchMessagesDebounced();
      // Forms and silent actions never open the stream, so its `done` event can't be
      // relied on to stop the spinner — clear it on anything that is not still running.
      // `AWAITING` counts: a run parked on a form is waiting for the user, and leaving
      // the spinner up there makes `onSendMessage` drop what they type into it.
      if (data.status != RUNNING_EXECUTION_STATUS) setIsLoading(false);
      setIsExecutionRunning(data.status == RUNNING_EXECUTION_STATUS);
    }
    else if (data.type == "CHAT_LOADING") {
      setIsLoading(true);
      setIsExecutionRunning(true);
    }
  };

  const onSendMessage = (data) => {

    const message = data.message || "";
    const chatOptions = buildChatOptions(executionVariables, data.chat_options);
    // A hidden message reaches the agent like any other, but the thread must not show
    // it — so there is no optimistic bubble to paint either.
    const isVisible = data.visible !== false;

    if (isVisible) {
      // Paint the user's message immediately while the request is in flight.
      scrollToBottom();
      setMessages([...messages, buildPendingUserMessage(
        messages,
        typeof message === "string" ? message : l("Enviando datos...%PA311")
      )]);
    }

    if (isLoading) return;
    setIsLoading(true);
    callSendMessage({
      chat_id: chat.id,
      message,
      attachments: data.attachments || [],
      visible: isVisible,
      ...(Object.keys(chatOptions).length > 0 ? { chat_options: chatOptions } : {}),
    });
  };

  // The whole page is capturable, not only the content area: the chat can itself live in
  // a modal portaled to the body (Studio's test chat), outside any layout container. The
  // root is <html> because the body measures 0px tall, and a drag is clamped to the root.
  const takeScreenshot = () => new Promise((resolve) => setScreenshotRequest({
    resolve,
    rootNode: document.documentElement,
  }));

  const onFinishScreenshot = (result) => {
    screenshotRequest?.resolve(result);
    setScreenshotRequest(null);
  };

  const takePhoto = () => new Promise((resolve) => setCameraRequest({ resolve }));

  const onFinishPhoto = (photo) => {
    cameraRequest.resolve(photo);
    setCameraRequest(null);
  };

  // Files dragged anywhere over the chat attach to the next answer. The overlay that
  // receives the drop covers the whole chat, so leaving it means leaving the chat.
  const onDragEnterChat = (event) => {
    if (!isFileDropAllowed || !event.dataTransfer.types.includes("Files")) return;
    setIsDraggingFiles(true);
  };

  const onDragLeaveFiles = () => setIsDraggingFiles(false);

  const onDropFiles = (event) => {
    event.preventDefault();
    setIsDraggingFiles(false);
    attachFiles(Array.from(event.dataTransfer.files), formRef.current, isMultipleAttachment(requestedSchema), flow?.id);
  };

  const onClickRollbackExecution = () => {
    callRollbackAction({ chat_id: chat.id });
    setIsLoading(true);
  };

  // Handing a component request to the host. It goes as an ACTION rather than an EVENT
  // because it is addressed to the host itself: it holds the request until the app
  // answers, and only it can turn that answer into the call that resumes the execution.
  // Outside the app mount there is no host, and a component tool has nowhere to paint.
  const onComponentRequest = (details) => {

    if (!host?.sendControllerAction) {
      toast(l("Esta conversación necesita la app del proyecto para continuar%CH006"), { type: "warning" });
      return;
    }

    host.sendControllerAction({
      type: ControllerType.ACTION,
      name: details.type == "SHOW_COMPONENT"
        ? ControllerActions.ShowComponent
        : ControllerActions.HideComponent,
      requestId: details.request_id,
      componentName: details.name,
      props: details.props || {},
      slotId,
      chatId: chat.id,
      nodeId: details.node_id,
      executionId: details.execution_id,
      messageId: details.message_id,
      sessionId: details.session_id,
      token: details.token,
    });

  };

  const onClickCancelChatAction = () => {
    callCancelChatAction({ chat_id: chat.id });
    setIsCancelable(false);
    setIsExecutionRunning(false);
    setIsLoading(true);
  };

  const onClickRegenerateMessage = (message, model) => {
    startStream();
    callRegenerateMessage({ message_id: message.id, model });
  };

  const onClickRepeatExecution = (messageId) => {
    startStream();
    callRepeatExecution({ message_id: messageId, session_id: executionSessionId });
  };

  useImperativeHandle(ref, () => ({
    cleanMessages: () => setMessages([]),
    setLoading: setIsLoading,
    refresh: refetchMessages,
    scrollToMessage,
    // Sending without the composer: the app writing on the user's behalf. `data` takes
    // the same shape the composer produces — { message, attachments, visible } — where
    // `message` may be a JSON payload rather than text, and `visible: false` keeps it
    // out of the thread.
    sendMessage: (data) => {
      if (isLoading) return;
      setIsLoading(true);
      if (data?.visible !== false) scrollToBottom();
      const chatOptions = buildChatOptions(executionVariables, data?.chat_options);
      callSendMessage({
        chat_id: chat.id,
        ...data,
        ...(Object.keys(chatOptions).length > 0 ? { chat_options: chatOptions } : {}),
      });
    },
  }));

  const visibleMessages = messages
    .filter(message => message.settings?.visible ?? true)
    .filter(message => message.isTemplate != true)
    .filter(message => !isRenderedByStream(message));

  // A placeholder left by a running process already animates that work is happening,
  // so neither the spinner nor the agent's own stream bubble goes under it: an agent
  // waiting on a tool that paints its steps would otherwise show two "thinking" at once.
  const isThinkingShown = visibleMessages[visibleMessages.length - 1]?.settings?.thinking_loading == true;

  const lastMessage = messages[messages.length - 1];
  const lastBotMessage = messages
    .filter(message => message.isTemplate != true && message.type == "BOT_MESSAGE")
    .slice(-1)[0];

  // The composer takes files when it asks for one, or for free text with the clip on it.
  const requestedSchema = lastBotMessage?.settings?.schema ?? {};
  const requestedInputType = requestedSchema.type ?? "TEXT";
  const isFileDropAllowed = !isReadOnly && (requestedInputType == "FILE" || requestedInputType == "TEXT");

  // chat.settings is the freshest source: init_web_chat / set_settings keep it in
  // sync with flow.chat_settings, while the flow prop is cached one level up and may
  // be stale after the user toggles something in the Settings view.
  const chatSettings = { ...(flow?.chatSettings ?? {}), ...(chat?.settings ?? {}) };

  // A continuous chat is the only one the user can start over from the toolbar: anywhere
  // else the thread ends when the page does, and there is nothing to walk away from.
  const isNewChatShown = chatSettings.keep_chat == true && !chat.is_debug && visibleMessages.length > 2;

  // hide_exit_process_button is for the product's chat: whoever runs a process from Studio
  // must always be able to stop it.
  const isStopShown = (isCancelable || isExecutionRunning) && (isExecutionChat || !chatSettings.hide_exit_process_button);

  const models = (reqModels.data?.models ?? [])
    .flatMap(group => group.models)
    .filter(model => model.generation_support?.text)
    .map(model => ({ name: model.name, publicName: model.public_name }));

  return <View
    activityText={activityText}
    attachmentOptions={getAttachmentOptions(showScreenshotAttachment)}
    cameraRequest={cameraRequest}
    chatRoomName={chatRoomName}
    chatSettings={chatSettings}
    executionRoomName={executionRoomName}
    flowId={flow?.id}
    formRef={formRef}
    historyComponent={historyComponent}
    isDeveloper={isDeveloper}
    isDraggingFiles={isDraggingFiles}
    isHistoryEnabled={isHistoryEnabled}
    isLoading={isLoading}
    isNewChatShown={isNewChatShown}
    isReadOnly={isReadOnly}
    isStopShown={isStopShown}
    isStreaming={isStreaming}
    isThinkingShown={isThinkingShown}
    lastBotMessage={lastBotMessage}
    lastMessage={lastMessage}
    listRef={listRef}
    messages={visibleMessages}
    models={models}
    rulesComponent={rulesComponent}
    screenshotRequest={screenshotRequest}
    scrollDirection={scrollDirection}
    streamingText={streamingText}
    takePhoto={takePhoto}
    takeScreenshot={takeScreenshot}
    thinkingText={thinkingText}
    onChatMessageReceived={onChatMessageReceived}
    onChatScroll={onChatScroll}
    onClickCancelChatAction={onClickCancelChatAction}
    onClickNewChat={onClickNewChat}
    onClickRegenerateMessage={onClickRegenerateMessage}
    onClickRepeatExecution={onClickRepeatExecution}
    onClickRollbackExecution={onClickRollbackExecution}
    onClickScrollBtn={onClickScrollBtn}
    onClickToggleHistory={onClickToggleHistory}
    onClickToggleRules={onClickToggleRules}
    onDragEnterChat={onDragEnterChat}
    onDragLeaveFiles={onDragLeaveFiles}
    onDropFiles={onDropFiles}
    onExecutionMessageReceived={onExecutionMessageReceived}
    onFinishPhoto={onFinishPhoto}
    onFinishScreenshot={onFinishScreenshot}
    onSendMessage={onSendMessage}
  />;

});

Conversation.displayName = "Conversation";

export default Conversation;
